import { computed, ref, unref, watch } from 'vue';
import RealEstateFinanceAPI from 'dashboard/api/realEstateFinance';
import RealEstateBrokerCommissionTransfersAPI from 'dashboard/api/realEstateBrokerCommissionTransfers';
import RealEstateFinanceCompanyTransfersAPI from 'dashboard/api/realEstateFinanceCompanyTransfers';

const createIdempotencyKey = () =>
  window.crypto?.randomUUID?.() ||
  `broker-transfer-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export function useRealEstateBrokerCommissionTransfer({
  beneficiaryId,
  beneficiaryType,
  installmentId,
  requestId,
  notify,
  t,
} = {}) {
  const detail = ref(null);
  const amount = ref('');
  const transferredOn = ref('');
  const observation = ref('');
  const receipt = ref(null);
  const loading = ref(false);
  const saving = ref(false);
  const uploadProgress = ref(0);
  const error = ref('');
  const idempotencyKey = ref(createIdempotencyKey());

  const beneficiary = computed(() => {
    const type = unref(beneficiaryType) || 'usuario';
    const id = unref(beneficiaryId);
    const beneficiaries = detail.value?.usuarios_agrupado || [];

    if (!id) {
      return beneficiaries.find(
        item => String(item.solicitacao_id) === String(unref(requestId))
      );
    }

    return beneficiaries.find(item => {
      const identity =
        type === 'empresa'
          ? item.empresa?.id || item.empresa_id
          : item.usuario?.id || item.usuario_id;
      return String(identity) === String(id);
    });
  });
  const isCompany = computed(
    () => (unref(beneficiaryType) || 'usuario') === 'empresa'
  );
  const selectedRequestId = computed(
    () => beneficiary.value?.solicitacao_id || unref(requestId)
  );
  const selectedCompanyId = computed(
    () =>
      beneficiary.value?.empresa?.id ||
      beneficiary.value?.empresa_id ||
      unref(beneficiaryId)
  );
  const amountCents = computed(() =>
    Math.round(Number(String(amount.value).replace(',', '.')) * 100)
  );
  const remainingCents = computed(
    () => beneficiary.value?.valor_restante?.amount_cents || 0
  );
  const canSubmit = computed(() =>
    Boolean(
      amountCents.value > 0 &&
        amountCents.value <= remainingCents.value &&
        (isCompany.value ? selectedCompanyId.value : selectedRequestId.value) &&
        transferredOn.value &&
        receipt.value
    )
  );

  const alert = (message, variant = 'error') => {
    error.value = variant === 'error' ? message : '';
    if (notify) notify(message, null, { variant });
  };

  const load = async () => {
    const id = unref(installmentId);
    if (!id) return;
    loading.value = true;
    try {
      const response = await RealEstateFinanceAPI.show(id);
      detail.value = response.data;
      amount.value = beneficiary.value?.valor_restante?.amount || '';
    } catch {
      alert(t('REAL_ESTATE_FINANCE.ERRORS.DETAIL'));
    } finally {
      loading.value = false;
    }
  };

  const submit = async () => {
    if (!canSubmit.value) return null;
    saving.value = true;
    uploadProgress.value = 0;
    try {
      const payload = {
        amountCents: amountCents.value,
        transferredOn: transferredOn.value,
        observation: observation.value.trim(),
        receipt: receipt.value,
        idempotencyKey: idempotencyKey.value,
      };
      const onUploadProgress = event => {
        uploadProgress.value = Math.round(
          (event.loaded / (event.total || event.loaded)) * 100
        );
      };
      const response = isCompany.value
        ? await RealEstateFinanceCompanyTransfersAPI.createTransfer(
            {
              ...payload,
              installmentId: unref(installmentId),
              agencyProfileId: selectedCompanyId.value,
            },
            onUploadProgress
          )
        : await RealEstateBrokerCommissionTransfersAPI.createTransfer(
            { ...payload, requestId: selectedRequestId.value },
            onUploadProgress
          );
      alert(t('REAL_ESTATE_FINANCE.ALERTS.TRANSFER_SAVED'), 'success');
      return response.data.data;
    } catch {
      alert(t('REAL_ESTATE_FINANCE.ERRORS.TRANSFER'));
      return null;
    } finally {
      saving.value = false;
    }
  };

  watch(
    [
      () => unref(beneficiaryId),
      () => unref(beneficiaryType),
      () => unref(installmentId),
      () => unref(requestId),
    ],
    load,
    { immediate: true }
  );

  return {
    detail,
    beneficiary,
    amount,
    amountCents,
    remainingCents,
    transferredOn,
    observation,
    receipt,
    loading,
    saving,
    selectedRequestId,
    selectedCompanyId,
    isCompany,
    uploadProgress,
    error,
    canSubmit,
    load,
    submit,
  };
}
